import type { NextPageContext } from 'next/types';
import includes from 'lodash/includes';
import min from 'lodash/min';
import round from 'lodash/round';
import reduce from 'lodash/reduce';
import map from 'lodash/map';
import compact from 'lodash/compact';
import minBy from 'lodash/minBy';
import fromPairs from 'lodash/fromPairs';
import sortBy from 'lodash/sortBy';
import isString from 'lodash/isString';
import every from 'lodash/every';
import { isNil } from 'lodash';

import { createApolloClient } from '@jerry-serverless/graphql-helpers/apollo/client';
import { SIGNUP_ENCRYPT_API_KEY_PREFIX } from '@jerry-serverless/insurance-common/constants/signup_encrypt_api_key_prefix';
import { captureException } from '@jerry-serverless/next-sentry';
import type { SignupChallengeStep } from '@jerry-serverless/enums/user_challenge';

import { USER_QUOTES_QUERY } from '../apollo/graphql/quote.query';
import { PolicyTierType, PolicyTypeType } from '../apollo/graphql/quote.query';
import { UserStageType } from '../apollo/graphql/quote.query';
import type {
  NormalizedPolicyTypeType,
  BundledQuoteType,
  QuotesType,
  UserWithQuotesType,
} from '../apollo/graphql/quote.query';
import { isInQuotePages } from '../steps';

import { CarrierID, getCarrierName } from './carrier';

const MAX_MONTHLY_PAYMENT_FOR_MOBILE = 126;
const DEFAULT_CHECKED_CARRIERS_COUNT = 10;
export const EMPTY_APP_QUOTES: QuotesType = {
  stage: UserStageType.QuotingEZLynx,
  bundledQuotes: [],
};

export interface PaymentPlanInfo {
  downPayment?: number;
  monthlyPayment?: number;
  monthlyPaymentCount?: number;
}

/**
 * Return app quotes data.
 */
export const getAppQuotes = async (
  context: NextPageContext
): Promise<{
  user: UserWithQuotesType;
  signupChallengeStep?: SignupChallengeStep;
} | null> => {
  const client = createApolloClient({ context, uri: process.env.NEXT_PUBLIC_API_URL });

  const headers: Record<string, string> = {};
  if (isInQuotePages(context.pathname)) {
    const encryptUserID = isString(context.query.user) ? context.query.user : '';
    if (encryptUserID) {
      headers.Authorization = [SIGNUP_ENCRYPT_API_KEY_PREFIX, encryptUserID].join(' ');
    }
  }

  try {
    const resp = await client.query({
      query: USER_QUOTES_QUERY,
      context: { headers },
    });
    const user = resp?.data?.viewer?.user;
    const signupChallengeStep = resp?.data?.viewer?.signupChallengeStep;
    if (user) {
      return { user, signupChallengeStep };
    }
    return null;
  } catch (err) {
    console.info(`${context.req?.url} getAppQuotes insuranceUser error:`, err);

    return null;
  }
};

export enum QuotingStatus {
  InProgress = 0,
  /** The quoting is still in progress, but there are quotes ready to display. */
  InProgressFoundQuotes = 1,
  Finished = 2,
}

export const getQuotingStatus = (userQuotes: QuotesType): QuotingStatus => {
  const { stage, isQuotingInProgress, bundledQuotes, currentBatchFinishedTiers } = userQuotes;

  if (includes([UserStageType.QuoteSent, UserStageType.SwitchBinding, UserStageType.SwitchPriceProvided], stage)) {
    return QuotingStatus.Finished;
  }

  if (isQuotingInProgress) {
    if (includes(currentBatchFinishedTiers, PolicyTierType.Basic) && bundledQuotes.length) {
      return QuotingStatus.InProgressFoundQuotes;
    } else {
      return QuotingStatus.InProgress;
    }
  }

  return includes([UserStageType.New, UserStageType.QuotingEZLynx, UserStageType.SwitchRequoting], stage)
    ? QuotingStatus.InProgress
    : QuotingStatus.Finished;
};

/**
 * Returns checked carriers count
 */
export const getCheckedCarriersCount = (userQuotes: QuotesType): number => {
  const { currentBatchCheckedCarriersCount } = userQuotes;

  return Math.max(currentBatchCheckedCarriersCount || 0, DEFAULT_CHECKED_CARRIERS_COUNT);
};

const MAX_PROGRESS = 0.8;
const MIN_PROGRESS = 0.2;
const MIDDLE_THRESHOLD = 3;
const MIDDLE_PROGRESS = 0.5;
/**
 * The progress number from the backend is only an estimate, added thresholds to make the progress number more reasonable.
 */
export const getQuotingProgress = (userQuotes: QuotesType, targetQuotingStatus: QuotingStatus): number => {
  const quotingStatus = getQuotingStatus(userQuotes);
  if (quotingStatus >= targetQuotingStatus) {
    return 100;
  }
  const quotesLength = userQuotes.bundledQuotes.length || 0;
  const progress = userQuotes.currentQuotingProgress;
  if (quotesLength === 0) {
    return MIN_PROGRESS * 100;
  }
  if (quotesLength <= MIDDLE_THRESHOLD) {
    return (min([MIDDLE_PROGRESS, progress]) || MIDDLE_PROGRESS) * 100;
  }
  return (min([MAX_PROGRESS, progress]) || MAX_PROGRESS) * 100;
};

/**
 * Return price of quote monthly payment
 */
export const getQuoteMonthlyPayment = (quote: BundledQuoteType): number => {
  return reduce(
    quote.policyEdges,
    (value, { policy }) => (value += round((policy.premium || 0) / (policy.term || 1), 2)),
    0
  );
};

/**
 * Return quote premium.
 */
export const getQuotePremium = (quote: BundledQuoteType): number => {
  return reduce(quote.policyEdges, (value, { policy }) => (value += policy.premium || 0), 0);
};

/**
 * Return term of quote.
 */
export const getQuoteTerm = (quote: BundledQuoteType): number => {
  const termFromQuote = Number(quote.term);
  if (quote.term && termFromQuote > 0) {
    return termFromQuote;
  }
  return reduce(quote.policyEdges, (acc, policyEdge) => Math.max(acc, policyEdge.policy.term ?? 0), 0);
};

/**
 * Return average price of specific carrier.
 */
export const getAveragePriceByCarrier = (quote: BundledQuoteType): number => {
  try {
    const carrierID = quote.policyEdges[0].policy.carrier.id;
    const price = reduce(
      quote.policyEdges,
      (value, { policy }) => {
        if (value !== -1 && policy.carrier.id === carrierID && policy.averagePriceByCarrier) {
          return value + policy.averagePriceByCarrier;
        } else {
          return -1;
        }
      },
      0
    );
    return price > 0 ? price : 0;
  } catch (err) {
    captureException(err);
    return 0;
  }
};

/**
 * Return user address.
 */
export const getUserAddressState = (quote: BundledQuoteType): string => {
  try {
    const address = quote.policyEdges[0].policy.userAddressSnapshot;
    const state = address && JSON.parse(address)?.state;
    return state;
  } catch (err) {
    return '';
  }
};

/**
 * Return average price of specific state.
 */
export const getAveragePriceByState = (quote: BundledQuoteType, state: string): number => {
  try {
    if (state) {
      const price = reduce(
        quote.policyEdges,
        (value, { policy }) => {
          if (
            value !== -1 &&
            policy.userAddressSnapshot &&
            JSON.parse(policy.userAddressSnapshot).state === state &&
            policy.averagePriceByState
          ) {
            return value + policy.averagePriceByState;
          } else {
            return -1;
          }
        },
        0
      );
      return price > 0 ? price : 0;
    }
    return 0;
  } catch (err) {
    captureException(err);
    return 0;
  }
};

/**
 * Return true, if monthly payment is less than 126
 */
export const isLessThan126ForMonthlyPayment = (quote: BundledQuoteType): boolean => {
  const monthlyPayment = getQuoteMonthlyPayment(quote);
  return monthlyPayment > 0 && monthlyPayment < MAX_MONTHLY_PAYMENT_FOR_MOBILE;
};

/**
 * Return the lowest monthly payment quote
 */
export const getLowestMonthlyPaymentQuote = (
  userQuotes: QuotesType,
  userSelectedTier?: PolicyTierType
): BundledQuoteType | undefined => {
  if (!isNil(userSelectedTier)) {
    const quotesInSelectedTier = userQuotes.bundledQuotes.filter(
      (bundledQuotes) =>
        bundledQuotes.policyEdges.find((edge) => edge.policy.type === 'car')?.policy.tier === userSelectedTier
    );
    if (quotesInSelectedTier.length > 0) {
      return minBy(quotesInSelectedTier, (quote) => {
        return getQuoteMonthlyPayment(quote) * 100;
      });
    }
  }
  const quotes = userQuotes.bundledQuotes;
  return minBy(quotes, (quote) => {
    return getQuoteMonthlyPayment(quote) * 100;
  });
};

/**
 * Return true, if there is a quote with monthly payment is less than 126
 */
export const hasQuoteWithLessThan126ForMonthlyPayment = (
  userQuotes: QuotesType,
  userSelectedTier?: PolicyTierType
): boolean => {
  const quote = getLowestMonthlyPaymentQuote(userQuotes, userSelectedTier);
  if (quote) {
    return isLessThan126ForMonthlyPayment(quote);
  }
  return false;
};

/**
 * Returns saving of quote
 */
export const getQuoteSavings = (quote: BundledQuoteType): number => Math.max(round(quote.savings || 0), 0);

/**
 * Return carrier name of quote
 */
export const getQuoteCarrierName = (quote: BundledQuoteType): string => {
  const carrierNames: string[] = compact(
    map(quote.policyEdges, ({ policy }) => {
      const originCarrierName = policy.carrier.nickname || policy.carrier.name || policy.carrierName || '';
      return getCarrierName(originCarrierName, policy.carrier.id);
    })
  );

  return carrierNames.join(' + ');
};

/**
 * Return carrier reviews of quote
 */
export const getCarrierReviews = (quote: BundledQuoteType): { reviews: number; rating: number } | undefined => {
  const results = compact(
    map(quote.policyEdges, ({ policy }) => {
      const { carrier } = policy;
      const { ratingReviews, ratingValue } = carrier;

      if (!ratingReviews || !ratingValue) {
        return;
      }

      return { reviews: ratingReviews, rating: ratingValue };
    })
  );

  return sortBy(results, ({ rating }) => rating)[0];
};

/**
 * Returns profile of carrier
 */
export const getQuoteCarrierProfiles = (quote: BundledQuoteType) => {
  const carriers = map(quote.policyEdges, ({ policy }) => {
    const { carrier } = policy;
    const { ratingReviews, ratingValue } = carrier;
    const originCarrierName = carrier.nickname || carrier.name || policy.carrierName || '';

    return {
      name: getCarrierName(originCarrierName, policy.carrier.id),
      id: policy.carrier.id,
      review: !!ratingReviews && !!ratingValue ? { reviews: ratingReviews, rating: ratingValue } : undefined,
      type: getNormalizedPolicyTypeType(policy.type),
    };
  });

  return sortBy(carriers, ({ type }) => (type === PolicyTypeType.Car ? 0 : 1));
};

/**
 * Covert PolicyTypeType to NormalizedPolicyTypeType
 */
export const getNormalizedPolicyTypeType = (policyType: PolicyTypeType): NormalizedPolicyTypeType => {
  switch (policyType) {
    case PolicyTypeType.Renter:
    case PolicyTypeType.Condo:
    case PolicyTypeType.Townhouse:
    case PolicyTypeType.Home:
      return PolicyTypeType.Home;
    case PolicyTypeType.Car:
      return PolicyTypeType.Car;
    default:
      return PolicyTypeType.Home;
  }
};

/**
 * Return the tier of quote
 */
export const geQuoteTier = (quote: BundledQuoteType): PolicyTierType => {
  const policyTypeTierMap = fromPairs(
    map(quote.policyEdges, (arg) => [getNormalizedPolicyTypeType(arg.policy.type), arg.policy.tier])
  );
  return policyTypeTierMap[PolicyTypeType.Car] || policyTypeTierMap[PolicyTypeType.Home] || PolicyTierType.Current;
};

/**
 * Return true, if all quotes are from Geico
 */
export const isGeicoQuotesOnly = (userQuotes: QuotesType): boolean => {
  const quotes = userQuotes.bundledQuotes;
  return every(quotes, (quote) => {
    return every(quote.policyEdges, ({ policy }) => policy.carrier.id === CarrierID.Geico);
  });
};
